import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // profileLoading: profil async yüklenirken true — authReady'yi bloklar
  const [profileLoading, setProfileLoading] = useState(false);
  const mountedRef = useRef(true);
  // Mevcut user ID'yi takip et — gereksiz state güncellemelerini önler
  const currentUserIdRef = useRef(null);

  /**
   * Profil verisini DB'den çeker, state'e YAZMAZ — sadece veriyi döner.
   */
  const fetchProfileData = useCallback(async (userId) => {
    try {
      const { data, error } = await supabase
        .from("user_profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (error) {
        console.error("[Auth] Profil çekme hatası:", error.message, error.code);
        return null;
      }

      return data;
    } catch (err) {
      console.error("[Auth] Profil çekme hatası:", err);
      return null;
    }
  }, []);

  // ─── 1) onAuthStateChange: sadece user ve loading state'ini yönetir ───
  useEffect(() => {
    mountedRef.current = true;
    let initialDone = false;

    // Güvenlik zaman aşımı - auth takılırsa sonsuz yükleme engellenir
    const safetyTimeout = setTimeout(() => {
      if (mountedRef.current && !initialDone) {
        console.warn("[Auth] Başlatma zaman aşımı (10s), loading=false yapılıyor");
        initialDone = true;
        setLoading(false);
      }
    }, 10000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mountedRef.current) return;

        const newUserId = session?.user?.id ?? null;

        console.log("[Auth] Event:", event, session?.user?.email || "no session");

        if (event === "INITIAL_SESSION") {
          // İlk session bilgisi
          currentUserIdRef.current = newUserId;
          if (session?.user) {
            setProfileLoading(true);
            setUser(session.user);
          } else {
            setUser(null);
            setProfile(null);
          }
          initialDone = true;
          clearTimeout(safetyTimeout);
          setLoading(false);

        } else if (event === "SIGNED_IN") {
          // SIGNED_IN, INITIAL_SESSION'dan hemen sonra aynı user ile gelirse atla
          if (newUserId === currentUserIdRef.current) {
            console.log("[Auth] SIGNED_IN atlandı (aynı user, zaten INITIAL_SESSION ile işlendi)");
            return;
          }
          // Farklı bir user ile giriş yapıldı (login form'dan)
          currentUserIdRef.current = newUserId;
          setProfileLoading(true);
          setUser(session?.user ?? null);

        } else if (event === "SIGNED_OUT") {
          currentUserIdRef.current = null;
          setUser(null);
          setProfile(null);
          setProfileLoading(false);

        } else if (event === "TOKEN_REFRESHED") {
          // Token yenilendi — user state'ini güncelleme (gereksiz re-render)
          // Sadece ref'i güncelle
          if (session?.user) {
            currentUserIdRef.current = session.user.id;
          }

        } else if (event === "USER_UPDATED") {
          if (session?.user) {
            currentUserIdRef.current = session.user.id;
            setUser(session.user);
          }
        }
      }
    );

    return () => {
      mountedRef.current = false;
      clearTimeout(safetyTimeout);
      subscription?.unsubscribe();
    };
  }, []);

  // ─── 2) Profil fetch: user.id değiştiğinde ayrı effect'te çalışır ───
  // user?.id kullanarak obje referansı yerine ID bazlı tetikleme yapıyoruz
  const userId = user?.id;
  useEffect(() => {
    if (!userId) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }

    let cancelled = false;
    setProfileLoading(true);

    const loadProfile = async () => {
      const profileData = await fetchProfileData(userId);
      if (cancelled || !mountedRef.current) return;

      console.log("[Auth] Profile loaded:", profileData?.role, profileData?.roles);
      setProfile(profileData);
      setProfileLoading(false);
    };

    loadProfile();

    return () => {
      cancelled = true;
    };
  }, [userId, fetchProfileData]);

  // Giriş yap
  const login = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
    return data;
  }, []);

  // Kayıt ol
  const register = useCallback(async (email, password, metadata) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
      },
    });

    if (error) throw error;
    return data;
  }, []);

  // Çıkış yap
  const logout = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUser(null);
    setProfile(null);
  }, []);

  // Profil güncelle
  const updateProfile = useCallback(async (updates) => {
    if (!user) throw new Error("Kullanıcı giriş yapmamış");

    const { data, error } = await supabase
      .from("user_profiles")
      .update(updates)
      .eq("id", user.id)
      .select()
      .single();

    if (error) throw error;
    setProfile(data);
    return data;
  }, [user]);

  // Yardımcı fonksiyonlar - Çoklu rol desteği
  const isInvestor = profile?.role === "investor" || profile?.roles?.includes("investor");
  const isStaff = profile?.role === "staff" || profile?.roles?.includes("staff");
  const isAdmin = profile?.role === "admin" || profile?.roles?.includes("admin");

  // Auth veya profil yüklenirken false — korumalı route'lar bunu beklemeli
  const authReady = !loading && !profileLoading;

  const value = {
    user,
    profile,
    loading,
    profileLoading,
    authReady,
    login,
    register,
    logout,
    updateProfile,
    isInvestor,
    isStaff,
    isAdmin,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}

export default AuthContext;
